import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRightIcon,
  HeartIcon,
  UsersIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import ImpactCard from "../components/ImpactCard";
import FoodCard from "../components/FoodCard";
import StoryCard from "../components/StoryCard";
import useCollection from "../hooks/useCollection";
import ClaimFoodModal from "../components/ClaimFoodModal";
import RequestFoodModal from "../components/RequestFoodModal";
import DeliveryModal from "../components/DeliveryModal";
import { useAuth } from "../context/AuthContext";
import { TABLES } from "../supabase";
import { AuthTokenRedirect } from "../components/AuthTokenRedirect";
import { useImpactStats } from "../hooks/useImpactStats";
import { useFoodListings } from "../hooks/useFoodListings";
import { useCommunityStories } from "../hooks/useCommunityStories";

export default function Home() {
  const { user } = useAuth();

  // Modal states
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState<any>(null);

  // Load Supabase data for food listings, categories, users, stories (collections)
  const { documents: foodCategories = [], loading: categoriesLoading } =
    useCollection(TABLES.FOOD_CATEGORIES);
  const { documents: users = [], loading: usersLoading } = useCollection(
    TABLES.USERS
  );

  // Use React Query for better caching
  const { data: foodListings = [], isLoading: listingsLoading } =
    useFoodListings();
  const { data: communityStories = [], isLoading: storiesLoading } =
    useCommunityStories();
  const { data: impactStats, isLoading: impactLoading } = useImpactStats();

  // Transform impact stats for display
  const displayStats = impactStats
    ? {
        totalMealsShared: (impactStats as any).total_meals_shared || 0,
        totalUsersActive:
          ((impactStats as any).total_donors || 0) +
          ((impactStats as any).total_recipients || 0) +
          ((impactStats as any).total_volunteers || 0),
        totalFoodWasteSaved: (impactStats as any).waste_reduced_kg || 0,
        co2Saved: (impactStats as any).co2_emissions_saved || 0,
      }
    : {
        totalMealsShared: 0,
        totalUsersActive: 0,
        totalFoodWasteSaved: 0,
        co2Saved: 0,
      };

  // Pick top 3 for featured, top 2 for stories
  const featuredListings = (foodListings as any[]).slice(0, 3);
  const featuredStories = (communityStories as any[]).slice(0, 2);

  const handleClaim = (listing: any) => {
    if (!user) {
      alert("Please sign in to claim food.");
      return;
    }
    setSelectedListing(listing);
    setClaimModalOpen(true);
  };

  const handleRequest = (listing: any) => {
    if (!user) {
      alert("Please sign in to request food.");
      return;
    }
    setSelectedListing(listing);
    setRequestModalOpen(true);
  };

  const handleDelivery = (listing: any) => {
    if (!user) {
      alert("Please sign in to request delivery.");
      return;
    }
    setSelectedListing(listing);
    setDeliveryModalOpen(true);
  };
  return (
    <div className="min-h-screen">
      <AuthTokenRedirect />
      {/* Hero Section */}
      <section className="relative flex items-center justify-center min-h-screen overflow-hidden text-white">
        {/* Enhanced Background with multiple gradients */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-teal-700 to-cyan-600"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/20 via-transparent to-cyan-500/20"></div>
        <div className="absolute inset-0 bg-gradient-to-bl from-transparent via-teal-500/10 to-emerald-500/20"></div>

        {/* Animated geometric patterns */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2240%22%20height%3D%2240%22%20viewBox%3D%220%200%2040%2040%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M20%2020c0-5.5-4.5-10-10-10s-10%204.5-10%2010%204.5%2010%2010%2010%2010-4.5%2010-10z%22/%3E%3C/g%3E%3C/svg%3E')] opacity-30"></div>

        {/* Enhanced floating elements with more variety */}
        <div className="absolute w-40 h-40 rounded-full shadow-2xl top-20 left-10 bg-gradient-to-br from-white/10 to-emerald-400/20 animate-float-gentle"></div>
        <div className="absolute w-32 h-32 rounded-full shadow-xl top-40 right-20 bg-gradient-to-br from-cyan-400/20 to-teal-400/30 animate-float-gentle animate-float-delay-2s"></div>
        <div className="absolute w-24 h-24 rounded-full shadow-lg bottom-32 left-1/4 bg-gradient-to-br from-emerald-400/20 to-cyan-400/20 animate-float-gentle animate-float-delay-4s"></div>
        <div className="absolute rounded-full shadow-xl w-28 h-28 top-60 right-1/3 bg-gradient-to-br from-teal-400/20 to-emerald-400/20 animate-float-gentle animate-float-delay-3s"></div>
        <div className="absolute w-20 h-20 rounded-full shadow-lg top-80 left-1/2 bg-gradient-to-br from-cyan-400/15 to-white/10 animate-float-gentle animate-float-delay-5s"></div>
        <div className="absolute w-16 h-16 rounded-full shadow-md bottom-20 right-10 bg-gradient-to-br from-emerald-400/15 to-teal-400/15 animate-float-gentle animate-float-delay-1s"></div>

        {/* Animated geometric shapes */}
        <div className="absolute w-12 h-12 rotate-45 shadow-lg top-32 right-1/4 bg-white/10 animate-spin-slow"></div>
        <div className="absolute w-8 h-8 rounded-full shadow-md bottom-40 left-20 bg-cyan-400/20 animate-pulse"></div>
        <div className="absolute w-6 h-6 rounded-full shadow-sm top-1/2 right-1/5 bg-emerald-400/20 animate-bounce-gentle"></div>

        {/* Glowing orbs */}
        <div className="absolute w-64 h-64 rounded-full top-1/4 left-1/4 bg-gradient-to-r from-emerald-400/10 to-cyan-400/10 blur-3xl animate-pulse"></div>
        <div className="absolute w-48 h-48 rounded-full bottom-1/4 right-1/4 bg-gradient-to-r from-teal-400/10 to-emerald-400/10 blur-2xl animate-pulse animate-pulse-delay-2s"></div>

        <div className="relative z-10 container-modern">
          <div className="text-center">
            {/* Enhanced badge with animation */}
            <div className="inline-flex items-center px-6 py-3 mb-8 transition-all duration-500 border-2 rounded-full shadow-2xl bg-white/20 backdrop-blur-xl border-white/30 hover:bg-white/30 animate-bounce-gentle">
              <span className="flex items-center gap-2 text-sm font-bold">
                <img
                  src="https://flagcdn.com/w40/lk.png"
                  alt="Sri Lanka Flag"
                  className="object-cover w-6 h-4 rounded-sm animate-pulse"
                />
                Made for Sri Lanka
              </span>
            </div>

            {/* Enhanced main heading with better typography */}
            <h1 className="mb-8 text-4xl font-bold md:text-5xl lg:text-6xl xl:text-7xl animate-fade-in">
              <span className="block mb-2 tracking-tight text-white">
                Bridge the Gap
              </span>
              <span className="block text-transparent bg-gradient-to-r from-emerald-200 via-teal-200 to-cyan-200 bg-clip-text animate-gradient-shift">
                Between Surplus & Need
              </span>
            </h1>

            {/* Enhanced description with better styling */}
            <p className="max-w-4xl mx-auto mb-12 text-lg font-medium leading-relaxed text-white/95 md:text-xl lg:text-2xl animate-slide-up">
              Join Sri Lanka's youth-led movement to reduce food waste and fight
              hunger. Connect surplus food with those who need it most.
            </p>

            {/* Enhanced CTA buttons with better styling */}
            <div className="flex flex-col justify-center gap-6 sm:flex-row animate-slide-up">
              <Link
                to="/find-food"
                className="relative px-8 py-4 overflow-hidden text-base font-bold text-white transition-all duration-500 transform shadow-2xl group bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-2xl hover:shadow-3xl hover:-translate-y-2 hover:scale-105"
              >
                <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 group-hover:opacity-100"></div>
                <span className="relative flex items-center justify-center gap-3">
                  <span className="text-xl">🔍</span>
                  Find Food Near You
                  <ArrowRightIcon className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>

              <Link
                to="/donate"
                className="relative px-8 py-4 text-base font-bold text-white transition-all duration-500 transform border-2 shadow-xl group bg-white/20 backdrop-blur-xl border-white/40 rounded-2xl hover:shadow-2xl hover:bg-white/30 hover:border-white/60 hover:-translate-y-2 hover:scale-105"
              >
                <span className="flex items-center justify-center gap-3">
                  <span className="text-xl transition-transform duration-300 group-hover:scale-110">
                    🍽️
                  </span>
                  Donate Surplus Food
                </span>
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-col items-center justify-center gap-8 mt-16 sm:flex-row text-white/80 animate-fade-in">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
                <span className="text-sm font-medium">1000+ Meals Shared</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
                <span className="text-sm font-medium">500+ Volunteers</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">50+ Locations</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute transform -translate-x-1/2 bottom-8 left-1/2 animate-bounce">
          <div className="flex justify-center w-6 h-10 border-2 rounded-full border-white/50">
            <div className="w-1 h-3 mt-2 rounded-full bg-white/70 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="section-modern bg-gradient-to-br from-slate-50 via-white to-emerald-50/40">
        <div className="container-modern">
          <div className="mb-20 text-center">
            <div className="inline-flex items-center px-6 py-3 mb-8 rounded-full shadow-lg bg-emerald-100">
              <span className="text-sm font-bold text-emerald-700">
                📊 Impact Dashboard
              </span>
            </div>
            <h2 className="mb-6 text-3xl font-bold text-slate-900 lg:text-4xl">
              Our Community Impact
            </h2>
            <p className="max-w-3xl mx-auto text-lg font-medium leading-relaxed text-slate-600">
              Together, we're making a real difference in reducing food waste
              and supporting communities across Sri Lanka.
            </p>
          </div>
          <div className="grid-modern">
            <ImpactCard
              title="Meals Shared"
              value={
                impactLoading
                  ? "..."
                  : displayStats.totalMealsShared?.toLocaleString?.() ?? "0"
              }
              subtitle="Nutritious meals distributed"
              icon={<HeartIcon className="w-6 h-6" />}
              color="primary"
              trend={{ value: 23, label: "vs last month" }}
            />
            <ImpactCard
              title="Active Users"
              value={
                impactLoading
                  ? "..."
                  : displayStats.totalUsersActive?.toLocaleString?.() ?? "0"
              }
              subtitle="Community members"
              icon={<UsersIcon className="w-6 h-6" />}
              color="accent"
              trend={{ value: 15, label: "vs last month" }}
            />
            <ImpactCard
              title="Food Waste Saved"
              value={
                impactLoading
                  ? "..."
                  : displayStats.totalFoodWasteSaved
                  ? `${displayStats.totalFoodWasteSaved.toLocaleString()}kg`
                  : "0kg"
              }
              subtitle="Prevented from landfills"
              icon={<GlobeAltIcon className="w-6 h-6" />}
              color="secondary"
              trend={{ value: 31, label: "vs last month" }}
            />
            <ImpactCard
              title="CO₂ Reduced"
              value={
                impactLoading
                  ? "..."
                  : displayStats.co2Saved
                  ? `${displayStats.co2Saved.toLocaleString()}kg`
                  : "0kg"
              }
              subtitle="Environmental impact"
              icon={<GlobeAltIcon className="w-6 h-6" />}
              color="primary"
              trend={{ value: 18, label: "vs last month" }}
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-modern bg-gradient-to-br from-emerald-50/50 via-white to-slate-50">
        <div className="container-modern">
          <div className="mb-20 text-center">
            <div className="inline-flex items-center px-6 py-3 mb-8 rounded-full shadow-lg bg-emerald-100">
              <span className="text-sm font-bold text-emerald-700">
                ⚡ How It Works
              </span>
            </div>
            <h2 className="mb-6 text-3xl font-bold text-slate-900 lg:text-4xl">
              Simple Steps, Big Impact
            </h2>
            <p className="max-w-3xl mx-auto text-lg font-medium leading-relaxed text-slate-600">
              Join the movement in just three easy steps and start making a
              difference in your community today.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-12 lg:gap-16 md:grid-cols-3">
            {/* Step 1 */}
            <div className="text-center group">
              <div className="relative mb-10">
                <div className="flex items-center justify-center w-32 h-32 mx-auto transition-all duration-500 transform shadow-2xl rounded-3xl bg-gradient-to-br from-emerald-100 to-teal-200 group-hover:shadow-3xl group-hover:scale-110 group-hover:rotate-3">
                  <span className="text-5xl">🍽️</span>
                </div>
                <div className="absolute flex items-center justify-center w-10 h-10 text-lg font-bold text-white rounded-full shadow-xl -top-3 -right-3 bg-gradient-to-r from-emerald-500 to-teal-600">
                  1
                </div>
              </div>
              <h3 className="mb-4 text-xl font-bold transition-colors duration-300 text-slate-900 group-hover:text-emerald-600">
                List Surplus Food
              </h3>
              <p className="text-base font-medium leading-relaxed text-slate-600">
                Restaurants, bakeries, and households can easily list their
                surplus food with photos, expiry times, and pickup details.
              </p>
            </div>
            {/* Step 2 */}
            <div className="text-center group">
              <div className="relative mb-10">
                <div className="flex items-center justify-center w-32 h-32 mx-auto transition-all duration-500 transform shadow-2xl rounded-3xl bg-gradient-to-br from-amber-100 to-orange-200 group-hover:shadow-3xl group-hover:scale-110 group-hover:rotate-3">
                  <span className="text-5xl">🔍</span>
                </div>
                <div className="absolute flex items-center justify-center w-10 h-10 text-lg font-bold text-white rounded-full shadow-xl -top-3 -right-3 bg-gradient-to-r from-amber-500 to-orange-600">
                  2
                </div>
              </div>
              <h3 className="mb-4 text-xl font-bold transition-colors duration-300 text-slate-900 group-hover:text-amber-600">
                Find & Request
              </h3>
              <p className="text-base font-medium leading-relaxed text-slate-600">
                Recipients browse available food by location, request items they
                need, and get matched with nearby donations.
              </p>
            </div>
            {/* Step 3 */}
            <div className="text-center group">
              <div className="relative mb-10">
                <div className="flex items-center justify-center w-32 h-32 mx-auto transition-all duration-500 transform shadow-2xl rounded-3xl bg-gradient-to-br from-cyan-100 to-blue-200 group-hover:shadow-3xl group-hover:scale-110 group-hover:rotate-3">
                  <span className="text-5xl">🚚</span>
                </div>
                <div className="absolute flex items-center justify-center w-10 h-10 text-lg font-bold text-white rounded-full shadow-xl -top-3 -right-3 bg-gradient-to-r from-cyan-500 to-blue-600">
                  3
                </div>
              </div>
              <h3 className="mb-4 text-xl font-bold transition-colors duration-300 text-slate-900 group-hover:text-cyan-600">
                Pickup & Deliver
              </h3>
              <p className="text-base font-medium leading-relaxed text-slate-600">
                Volunteers help with deliveries, or recipients can arrange
                direct pickup. Everyone wins!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Food Listings */}
      <section className="section-modern bg-gradient-to-br from-slate-50 via-white to-cyan-50/30">
        <div className="container-modern">
          <div className="mb-16 flex-modern">
            <div>
              <div className="inline-flex items-center px-6 py-3 mb-6 rounded-full shadow-lg bg-cyan-100">
                <span className="text-sm font-bold text-cyan-700">
                  🍽️ Fresh Food
                </span>
              </div>
              <h2 className="mb-4 text-3xl font-bold text-slate-900 lg:text-4xl">
                Available Food Near You
              </h2>
              <p className="text-lg font-medium text-slate-600">
                Fresh donations from your community
              </p>
            </div>
            <Link to="/find-food" className="px-4 py-2 text-sm btn-outline">
              <span>View All</span>
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </Link>
          </div>

          {listingsLoading || categoriesLoading || usersLoading ? (
            <div className="py-20 text-center">
              <div className="inline-flex items-center px-8 py-4 shadow-lg bg-slate-100 rounded-2xl">
                <div className="w-8 h-8 mr-4 rounded-full border-3 border-emerald-500 border-t-transparent animate-spin"></div>
                <span className="text-lg font-bold text-slate-600">
                  Loading fresh food...
                </span>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 lg:gap-12 md:grid-cols-2 xl:grid-cols-3">
              {featuredListings.map((listing) => (
                <FoodCard
                  key={listing.id}
                  listing={listing}
                  foodCategories={foodCategories}
                  users={users}
                  onClaim={() => handleClaim(listing)}
                  onRequest={() => handleRequest(listing)}
                  onDelivery={() => handleDelivery(listing)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Community Stories */}
      <section className="section-modern bg-gradient-to-br from-purple-50/50 via-white to-pink-50/30">
        <div className="container-modern">
          <div className="mb-16 flex-modern">
            <div>
              <div className="inline-flex items-center px-6 py-3 mb-6 bg-purple-100 rounded-full shadow-lg">
                <span className="text-sm font-bold text-purple-700">
                  💚 Community
                </span>
              </div>
              <h2 className="mb-4 text-3xl font-bold text-slate-900 lg:text-4xl">
                Community Stories
              </h2>
              <p className="text-lg font-medium text-slate-600">
                Real impact from real people
              </p>
            </div>
            <Link to="/community" className="px-4 py-2 text-sm btn-outline">
              <span>Read More</span>
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </Link>
          </div>

          {storiesLoading ? (
            <div className="py-20 text-center">
              <div className="inline-flex items-center px-8 py-4 shadow-lg bg-slate-100 rounded-2xl">
                <div className="w-8 h-8 mr-4 border-purple-500 rounded-full border-3 border-t-transparent animate-spin"></div>
                <span className="text-lg font-bold text-slate-600">
                  Loading stories...
                </span>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              {featuredStories.map((story) => (
                <StoryCard
                  key={story.id}
                  story={story}
                  onLike={(id) => console.log("Like:", id)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="relative py-32 overflow-hidden text-white lg:py-40">
        {/* Background with gradient and pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-teal-700 to-cyan-600"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50"></div>

        {/* Static decorative elements */}
        <div className="absolute w-20 h-20 rounded-full top-10 right-10 bg-white/10"></div>
        <div className="absolute w-16 h-16 rounded-full bottom-20 left-20 bg-cyan-400/20"></div>

        <div className="relative z-10 text-center container-modern">
          <div className="inline-flex items-center px-6 py-3 mb-10 border-2 rounded-full shadow-2xl bg-white/20 backdrop-blur-xl border-white/30">
            <span className="text-sm font-bold">🚀 Join the Movement</span>
          </div>

          <h2 className="mb-6 text-3xl font-bold md:text-4xl lg:text-5xl">
            Ready to Make a Difference?
          </h2>

          <p className="max-w-3xl mx-auto mb-8 text-lg font-medium leading-relaxed text-white/95 md:text-xl">
            Join thousands of Sri Lankans who are already part of the MealBridge
            community. Every meal shared is a step towards a more sustainable
            future.
          </p>

          <div className="flex flex-col justify-center gap-8 sm:flex-row">
            <Link to="/auth" className="px-4 py-2 text-sm btn-gradient">
              🎯 Join MealBridge Today
            </Link>
            <Link
              to="/auth"
              className="px-4 py-2 text-sm font-bold text-white transition-all duration-500 transform border-2 rounded-lg shadow-lg bg-white/20 backdrop-blur-xl border-white/40 hover:bg-white/30 hover:border-white/60 hover:-translate-y-1 hover:scale-105 hover:shadow-xl"
            >
              🤝 Become a Volunteer
            </Link>
          </div>
        </div>
      </section>

      {/* Modals */}
      {selectedListing && (
        <>
          <ClaimFoodModal
            isOpen={claimModalOpen}
            onClose={() => {
              setClaimModalOpen(false);
              setSelectedListing(null);
            }}
            listing={selectedListing}
          />
          <RequestFoodModal
            isOpen={requestModalOpen}
            onClose={() => {
              setRequestModalOpen(false);
              setSelectedListing(null);
            }}
            listing={selectedListing}
          />
          <DeliveryModal
            isOpen={deliveryModalOpen}
            onClose={() => {
              setDeliveryModalOpen(false);
              setSelectedListing(null);
            }}
            listing={selectedListing}
          />
        </>
      )}
    </div>
  );
}
